import { Fieldset, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  CONTACT_CATEGORIES,
  CONTACT_CATEGORY_KEYS,
  ContactCategory,
  ContactSubCategory,
} from 'src/Constants/ContactCategories.constants';
import { getPresetContactKind } from 'src/Utils/contactKindPreset';

import styles from './CategorySectionForm.module.scss';
import { Props } from './CategorySectionForm.types';

// Translation keys for categories
// t('contacts.categories.Family & Community', 'Famille & Communauté')
// t('contacts.categories.Professionals & Services', 'Professionnels & Services')
// t('contacts.categories.Emergencies & Institutions', 'Urgences & Institutions')

// Translation keys for subcategories - Family & Community
// t('contacts.subcategories.Family Member Owner / Co-owner', 'Membre Familial Propriétaire / Copropriétaire')
// t('contacts.subcategories.Family Member User (non-owner)', 'Membre Familial Usager (non-propriétaire)')
// t('contacts.subcategories.Seasonal / Long-term Tenant', 'Locataire Saisonnier/Longue Durée')
// t('contacts.subcategories.Trusted Person / Caretaker', 'Personne de Confiance / Gardien')
// t('contacts.subcategories.Direct Neighbor (Emergency Contact)', "Voisin Direct (Contact d'urgence)")
// t('contacts.subcategories.Condominium Trustee (if applicable)', 'Syndic de Copropriété (si applicable)')

// Translation keys for subcategories - Professionals & Services
// t('contacts.subcategories.Plumber', 'Plombier')
// t('contacts.subcategories.Electrician', 'Électricien')
// t('contacts.subcategories.Mason', 'Maçon')
// t('contacts.subcategories.Roofer', 'Couvreur')
// t('contacts.subcategories.Heating Engineer / Boiler Maintenance', 'Chauffagiste / Entretien Chaudière')
// t('contacts.subcategories.Glazier', 'Vitrier')
// t('contacts.subcategories.Locksmith / Metalworker', 'Serrurerie / Métallier')
// t('contacts.subcategories.Architect', 'Architecte')
// t('contacts.subcategories.Project Manager / Works Supervisor', "Maître d'œuvre / Chef de projet Travaux")
// t('contacts.subcategories.Interior Decorator', "Décorateur d'intérieur")
// t('contacts.subcategories.Gardener / Landscaper', 'Jardinier / Paysagiste')
// t('contacts.subcategories.Cleaning Lady / Cleaning Service', 'Femme de ménage / Service de nettoyage')
// t('contacts.subcategories.Concierge / Home Help', 'Concierge / Aide à domicile')
// t('contacts.subcategories.Antenna Installer / TV Installation', 'Antenniste / Installation TV')
// t('contacts.subcategories.Alarm Installation / Home Automation', 'Installation alarme / Domotique')
// t('contacts.subcategories.Pool Maintenance', 'Entretien Piscine')
// t('contacts.subcategories.Chimney Sweep', 'Ramoneur')
// t('contacts.subcategories.Garage / Mechanic (if shared vehicles)', 'Garagiste / Mécanicien (si véhicules partagés)')
// t('contacts.subcategories.Electricity Supplier', "Fournisseur d'électricité")
// t('contacts.subcategories.Gas Supplier', 'Fournisseur de gaz')
// t('contacts.subcategories.Water Supplier', "Fournisseur d'eau")
// t('contacts.subcategories.Fuel / Wood / Pellets Supplier', 'Livreur de fioul / bois / pellets')
// t('contacts.subcategories.Internet Service Provider (ISP)', "Fournisseur d'accès Internet (FAI)")
// t('contacts.subcategories.Phone / TV Operator', 'Opérateur de téléphonie/TV')
// t('contacts.subcategories.Bank / Shared Property Account', 'Banque / Compte du bien partagé')
// t('contacts.subcategories.Home Insurance', 'Assureur habitation')
// t('contacts.subcategories.Grocery Delivery / Local Caterer', 'Livraison de courses / Traiteur local')
// t('contacts.subcategories.Notary (for succession / transmission)', 'Notaire (succession, indivision)')
// t('contacts.subcategories.Wealth Management Advisor', 'Conseiller en Gestion de Patrimoine (CGP)')
// t('contacts.subcategories.Lawyer (Real Estate, Family Law)', 'Avocat (droit immobilier, droit de la famille)')
// t('contacts.subcategories.Accountant / Tax Specialist', 'Expert-Comptable / Fiscaliste')
// t('contacts.subcategories.Real Estate Agent / Rental Manager', 'Agent Immobilier / Gestionnaire Locatif')
// t('contacts.subcategories.Professional Rental Manager (Agency)', 'Gestionnaire Locatif Professionnel (Agence)')
// t('contacts.subcategories.Bailiff', 'Huissier de Justice')
// t('contacts.subcategories.Shipyard / Wintering', 'Chantier Naval / Hivernage')
// t('contacts.subcategories.Marine Mechanic', 'Mécanicien Naval')
// t('contacts.subcategories.Harbor Master / Marina', 'Capitainerie / Port de Plaisance')
// t('contacts.subcategories.Fittings / Equipment Supplier', "Fournisseur d'Accastillage / Équipement")
// t('contacts.subcategories.Marine Insurance (Boat)', 'Assureur Maritime (Bateau)')
// t('contacts.subcategories.RV Dealer / Repairer', 'Concessionnaire / Réparateur Camping-car')
// t('contacts.subcategories.Technical Inspection (RV)', 'Contrôle Technique (Camping-car)')
// t('contacts.subcategories.Vehicle Insurance (RV)', 'Assureur Véhicule (Camping-car)')
// t('contacts.subcategories.Forestry Expert / Silvicultural Manager', 'Expert Forestier / Gestionnaire Sylvicole')
// t('contacts.subcategories.Forestry Cooperative / Wood Buyer', 'Coopérative Forestière / Acheteur de Bois')
// t('contacts.subcategories.Surveyor / Land Expert', 'Géomètre / Expert Foncier')
// t('contacts.subcategories.Veterinarian (Equine or General)', 'Vétérinaire (Équin ou Général)')
// t('contacts.subcategories.Farrier', 'Maréchal-Ferrant')
// t('contacts.subcategories.Stable / Equestrian Center (Boarding)', 'Écurie / Centre Équestre (Pension)')
// t('contacts.subcategories.Animal Feed Supplier', "Fournisseur d'Alimentation Animale")
// t('contacts.subcategories.Equine / Animal Insurance', 'Assureur Équin / Animaux')
// t('contacts.subcategories.Animal Trainer / Behaviorist', 'Éducateur / Comportementaliste Animal')

// Translation keys for subcategories - Emergencies & Institutions
// t('contacts.subcategories.Firefighters', 'Pompiers (18)')
// t('contacts.subcategories.Police / Gendarmerie', 'Police / Gendarmerie (17)')
// t('contacts.subcategories.Local General Practitioner', 'Médecin Traitant Local')
// t('contacts.subcategories.Hospital / Clinic', 'Hôpital / Clinique')
// t('contacts.subcategories.Pharmacy', 'Pharmacie')
// t('contacts.subcategories.Town Hall / Municipal Services', 'Mairie / Services Municipaux')

export const CategorySectionForm = ({ form, presetKind }: Props) => {
  const { t } = useTranslation();

  const selectedCategory = form.values.category;
  const subcategories = selectedCategory ? CONTACT_CATEGORIES[selectedCategory] : [];

  const applyPreset = (category?: ContactCategory, subcategory?: ContactSubCategory) => {
    if (presetKind && !form.values.kindTouched) {
      form.setFieldValue('kind', getPresetContactKind(category, subcategory));
    }
  };

  const handleCategoryChange = (value: string | null) => {
    const category = (value ?? undefined) as ContactCategory | undefined;
    form.setFieldValue('category', category);
    form.setFieldValue('subcategory', undefined);
    applyPreset(category, undefined);
  };

  const handleSubcategoryChange = (value: string | null) => {
    const subcategory = (value ?? undefined) as ContactSubCategory | undefined;
    form.setFieldValue('subcategory', subcategory);
    applyPreset(form.values.category, subcategory);
  };

  const categoryData = CONTACT_CATEGORY_KEYS.map(key => ({
    value: key,
    label: t(`contacts.categories.${key}`, key),
  }));

  const subcategoryData = subcategories.map(subcat => ({
    value: subcat,
    label: t(`contacts.subcategories.${subcat}`, subcat),
  }));

  return (
    <Fieldset legend={t('contacts.categorySection', 'Catégorie')}>
      <div className={styles.content}>
        <div className={styles.flexRow}>
          <Select
            label={t('contacts.category', 'Catégorie')}
            placeholder={t('contacts.selectCategory', 'Sélectionner une catégorie')}
            data={categoryData}
            value={form.values.category ?? null}
            onChange={handleCategoryChange}
            clearable
            searchable
          />
          <Select
            label={t('contacts.subcategory', 'Sous-catégorie')}
            placeholder={t('contacts.selectSubcategory', 'Sélectionner une sous-catégorie')}
            data={subcategoryData}
            value={form.values.subcategory ?? null}
            onChange={handleSubcategoryChange}
            disabled={!selectedCategory}
            clearable
            searchable
          />
        </div>
      </div>
    </Fieldset>
  );
};
